import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useEnvironment, AppType } from '../context/EnvironmentContext';
import { useAuth } from '../context/AuthContext';

const EnvironmentSelectionPage: React.FC = () => {
    const { setApp } = useEnvironment();
    const { user } = useAuth();
    const navigate = useNavigate();

    const handleSelect = React.useCallback((app: AppType) => {
        setApp(app);
        navigate('/');
    }, [setApp, navigate]);

    // Filter apps based on user permissions
    const allowedApps = React.useMemo(() => {
        return user?.allowedApps || [AppType.LEGACY, AppType.HAUSARZT, AppType.KINDERARZT];
    }, [user]);

    // If only one app is allowed, auto-redirect (handled in layout or route wrapper usually, but good to have here too)
    React.useEffect(() => {
        if (allowedApps.length === 1) {
            handleSelect(allowedApps[0]);
        }
    }, [allowedApps, handleSelect]);

    return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
            <h1 className="text-3xl font-bold mb-8 text-gray-800">Bereich wählen</h1>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl w-full px-4">
                {allowedApps.includes(AppType.LEGACY) && (
                    <button
                        onClick={() => handleSelect(AppType.LEGACY)}
                        className="flex flex-col items-center p-8 bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow border-2 border-transparent hover:border-blue-500"
                    >
                        <div className="w-48 h-32 flex items-center justify-center mb-6">
                            <img src="/logo_praxis_hayat_seite.webp" alt="Legacy Logo" className="max-h-full max-w-full" />
                        </div>
                        <h2 className="text-xl font-semibold text-gray-800">Legacy Version</h2>
                        <p className="text-gray-500 mt-2 text-center">Bisherige Version der Applikation</p>
                    </button>
                )}

                {allowedApps.includes(AppType.HAUSARZT) && (
                    <button
                        onClick={() => handleSelect(AppType.HAUSARZT)}
                        className="flex flex-col items-center p-8 bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow border-2 border-transparent hover:border-blue-500"
                    >
                        <div className="w-48 h-32 flex items-center justify-center mb-6">
                            <img src="/hausarzt_logo.svg" alt="Hausarzt Logo" className="max-h-full max-w-full" />
                        </div>
                        <h2 className="text-xl font-semibold text-gray-800">Panmedical Hausarzt Medizin</h2>
                        <p className="text-gray-500 mt-2 text-center">Planung für Hausärzte</p>
                    </button>
                )}

                {allowedApps.includes(AppType.KINDERARZT) && (
                    <button
                        onClick={() => handleSelect(AppType.KINDERARZT)}
                        className="flex flex-col items-center p-8 bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow border-2 border-transparent hover:border-blue-500"
                    >
                        <div className="w-48 h-32 flex items-center justify-center mb-6">
                            <img src="/kinder_logo.svg" alt="Kinderarzt Logo" className="max-h-full max-w-full" />
                        </div>
                        <h2 className="text-xl font-semibold text-gray-800">Panmedical Kindermedizin</h2>
                        <p className="text-gray-500 mt-2 text-center">Planung für Kinderärzte</p>
                    </button>
                )}
            </div>
        </div>
    );
};

export default EnvironmentSelectionPage;
